import type { AuthConfig, AuthUser } from '../types';

export class AuthenticationService {
  private provider: 'firebase' | 'azure';
  private firebaseAuth: any | null;
  private tokenRefreshInterval: number;
  private onTokenRefresh?: (token: string | null) => void;
  private onAuthStateChange?: (user: AuthUser | null, token: string | null) => void;
  private logger: Pick<Console, 'info' | 'warn' | 'error' | 'log'>;

  private currentUser: AuthUser | null = null;
  private currentToken: string | null = null;
  private refreshTimer: any = null;
  private tokenExpiryTime: Date | null = null;

  constructor({
    provider = 'firebase',
    firebaseAuth = null,
    tokenRefreshInterval = 50 * 60 * 1000,
    onTokenRefresh,
    onAuthStateChange,
    logger = console,
  }: AuthConfig) {
    this.provider = provider;
    this.firebaseAuth = firebaseAuth;
    this.tokenRefreshInterval = tokenRefreshInterval;
    this.onTokenRefresh = onTokenRefresh;
    this.onAuthStateChange = onAuthStateChange;
    this.logger = logger;

    this.initialize();
  }

  private initialize() {
    if (this.provider === 'firebase' && this.firebaseAuth) {
      this.initializeFirebase();
    }
  }

  private initializeFirebase() {
    if (!this.firebaseAuth) {
      this.logger.error('Firebase Auth instance not provided');
      return;
    }

    this.firebaseAuth.onAuthStateChanged(async (user: any) => {
      if (user) {
        await this.handleFirebaseUser(user);
      } else {
        this.clearAuthentication();
      }
    });
  }

  private async handleFirebaseUser(firebaseUser: any) {
    try {
      const token: string = await firebaseUser.getIdToken(true);
      const tokenResult = await firebaseUser.getIdTokenResult();

      this.currentUser = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        provider: 'firebase',
      };

      this.currentToken = token;
      this.tokenExpiryTime = new Date(tokenResult.expirationTime);

      this.onAuthStateChange?.(this.currentUser, this.currentToken);
      this.setupTokenRefresh();
      this.logger.info(`Firebase user authenticated: ${firebaseUser.email}`);
    } catch (error) {
      this.logger.error('Error handling Firebase user:', error as any);
      this.clearAuthentication();
    }
  }

  private setupTokenRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
    this.refreshTimer = setInterval(() => {
      this.refreshToken();
    }, this.tokenRefreshInterval);

    if (this.tokenExpiryTime) {
      const timeUntilExpiry = this.tokenExpiryTime.getTime() - Date.now();
      const refreshTime = Math.max(timeUntilExpiry - 5 * 60 * 1000, 0);
      if (refreshTime > 0) {
        setTimeout(() => this.refreshToken(), refreshTime);
      }
    }
  }

  private async refreshToken() {
    try {
      if (this.provider === 'firebase') {
        await this.refreshFirebaseToken();
      }
    } catch (error: any) {
      this.logger.error('Error refreshing token:', error);
      
      // Check if token refresh failed due to auth issues
      if (error?.code === 'auth/user-token-expired' ||
          error?.code === 'auth/invalid-user-token' ||
          error?.code === 'auth/user-disabled' ||
          error?.code === 'auth/requires-recent-login') {
        this.logger.warn('Firebase token renewal failed, logging out user');
        // Force logout on critical auth errors
        await this.signOut();
      }
    }
  }

  private async refreshFirebaseToken() {
    if (!this.firebaseAuth || !this.firebaseAuth.currentUser) {
      this.logger.warn('No Firebase user to refresh token for');
      // If we lost the user session, clear authentication
      if (this.currentUser && !this.firebaseAuth?.currentUser) {
        this.logger.warn('Firebase session lost, clearing authentication');
        this.clearAuthentication();
      }
      return;
    }
    
    try {
      const newToken: string = await this.firebaseAuth.currentUser.getIdToken(true);
      const tokenResult = await this.firebaseAuth.currentUser.getIdTokenResult();
      this.currentToken = newToken;
      this.tokenExpiryTime = new Date(tokenResult.expirationTime);
      this.onTokenRefresh?.(newToken);
      this.logger.info('Firebase token refreshed successfully');
    } catch (error: any) {
      this.logger.error('Failed to refresh Firebase token:', error);
      // Re-throw to let the caller handle it
      throw error;
    }
  }

  async getToken(forceRefresh = false): Promise<string | null> {
    try {
      if (forceRefresh) {
        await this.refreshToken();
      }
      if (this.tokenExpiryTime) {
        const timeUntilExpiry = this.tokenExpiryTime.getTime() - Date.now();
        if (timeUntilExpiry < 5 * 60 * 1000) {
          await this.refreshToken();
        }
      }
      return this.currentToken;
    } catch (error: any) {
      this.logger.error('Error getting token:', error);
      // If token retrieval fails critically, force logout
      if (error?.code === 'auth/user-token-expired' ||
          error?.code === 'auth/invalid-user-token') {
        await this.signOut();
      }
      return null;
    }
  }

  getUser(): AuthUser | null {
    return this.currentUser;
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null && this.currentToken !== null;
  }

  async signOut() {
    try {
      if (this.provider === 'firebase' && this.firebaseAuth) {
        await this.firebaseAuth.signOut();
        
        // Clear any persistent storage that might keep the user logged in
        try {
          // Clear localStorage keys that Firebase might use
          const firebaseKeys = Object.keys(localStorage).filter(key => 
            key.startsWith('firebase:') || key.includes('firebase')
          );
          firebaseKeys.forEach(key => localStorage.removeItem(key));
          
          // Also clear sessionStorage
          const sessionKeys = Object.keys(sessionStorage).filter(key => 
            key.startsWith('firebase:') || key.includes('firebase')
          );
          sessionKeys.forEach(key => sessionStorage.removeItem(key));
        } catch (storageError) {
          this.logger.warn('Could not clear Firebase storage:', storageError);
        }
      }
      this.clearAuthentication();
      this.logger.info('Logout successful - cleared authentication state');
    } catch (error) {
      this.logger.error('Error signing out:', error as any);
      throw error;
    }
  }

  private clearAuthentication() {
    this.currentUser = null;
    this.currentToken = null;
    this.tokenExpiryTime = null;
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.onAuthStateChange?.(null, null);
  }

  async authenticatedFetch(url: RequestInfo | URL, options: RequestInit = {}) {
    const token = await this.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }
    const headers = {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
    } as Record<string, string>;

    const exec = async () => fetch(url, { ...options, headers });

    let response = await exec();
    if (response.status === 401) {
      this.logger.info('Got 401, attempting token refresh and retry');
      await this.refreshToken();
      const newToken = await this.getToken();
      if (newToken) {
        headers.Authorization = `Bearer ${newToken}`;
        response = await exec();
      }
    }
    return response;
  }

  destroy() {
    this.clearAuthentication();
  }
}
